import { z } from "zod";
import {
  FirestoreTimestampSchema,
  RequiredFirestoreTimestampSchema,
} from "./common.schema";

export const WordPressDomainIntegrationSchema = z.object({
  site_id: z.string(),
  site_url: z.string(),
  connectedBy: z.string(),
  connectedAt: RequiredFirestoreTimestampSchema,
  sync_status: z
    .enum(["pending", "in_progress", "completed", "error"])
    .default("pending"),
  last_sync_at: FirestoreTimestampSchema.optional(),
  last_sync_attempt_at: FirestoreTimestampSchema.optional(),
  next_publish_not_before: FirestoreTimestampSchema.optional(),
  publish_cooldown_seconds: z.number().default(120),
  total_items_synced: z.number().default(0),
  synced_types: z.record(z.string(), z.number()).optional(),
  error_message: z.string().nullable().optional(),
});

export type WordPressDomainIntegration = z.infer<
  typeof WordPressDomainIntegrationSchema
>;

export const parseWordPressDomainIntegration = (
  data: unknown,
): WordPressDomainIntegration => WordPressDomainIntegrationSchema.parse(data);

/**
 * Tolerant view of the same document, for reading only.
 *
 * `core` can create this document from its sync error path
 * (`_mark_error` -> `save_wordpress_integration`, a `set(..., merge=True)` on a
 * doc that does not exist yet) before `ensure_integration_initialized` ever
 * runs, so the stored doc can legitimately be missing `site_url`,
 * `connectedBy` and `connectedAt`. That is a broken integration, not a healthy
 * one -- but dropping it means the failure is invisible in the UI. Parse it
 * with this schema and gate "is it connected?" on
 * {@link isWordPressConnected}; never write this shape.
 *
 * The strict schema above stays the drift-checked mirror of core's Pydantic
 * model -- do not relax it.
 */
export const PartialWordPressDomainIntegrationSchema =
  WordPressDomainIntegrationSchema.partial({
    site_url: true,
    connectedBy: true,
    connectedAt: true,
  });

export type PartialWordPressDomainIntegration = z.infer<
  typeof PartialWordPressDomainIntegrationSchema
>;

/** True only for a document that satisfies core's full required-field set. */
export const isWordPressConnected = (
  integration: PartialWordPressDomainIntegration | null | undefined,
): boolean => WordPressDomainIntegrationSchema.safeParse(integration).success;
